
import RadioGroup from '@/components/forms/RadioGroup.vue'
import environment from '@/helpers/Environment'
import { findCurrency } from '@/helpers/Currency'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import Price from '@/components/elements/Price.vue'
import type { AnnotatedSession } from '@/types/Sessions'
import type { LanguageStrings } from '@/language/types'
import type { SessionPickerTabsConfig } from '@/api/types/processedEntities'
import Tabs, { Tab } from '@/components/elements/Tabs.vue'
import { toKebabCase } from '@/helpers/StringHelpers'

interface Item {
  session: AnnotatedSession
  badges: Badge[]
  label: string
  // TODO Move to <AvailabilityStatus>.
  availability: {
    label: string
    classes: Dict<boolean>
  }
}

interface SessionTab extends Tab {
  threshold?: string
  sessions: AnnotatedSession[]
}

interface Badge {
  symbol?: string
  style: 'increase' | MessageStyle
}

@Component({
  name: 'SelectAvailableSession',
  components: { Tabs, Price, RadioGroup },
})
export default class extends Vue {
  @Prop({ required: true })
  eventSessions: AnnotatedSession[]

  @Prop()
  config: SessionPickerTabsConfig

  @Prop()
  value: AnnotatedSession

  // TODO Replace with some deep-reaching CSS in <ReserveQuantityFirst>?
  @Prop()
  hideLabel: boolean

  @Prop()
  showPrices: boolean

  @Prop()
  showSoldOutTimes: boolean

  @Prop()
  capacityThreshold: number | undefined

  t: LanguageStrings['selectAvailableSession']

  selectedTabId: string | null = null

  mounted() {
    // Only tabs with subtitles do not have sessions
    const firstTabWithSessions = this.tabs.find((t) => !t.subtitle)
    if (firstTabWithSessions) {
      this.selectedTabId = firstTabWithSessions.id
    }
  }

  get sessions(): Item[] {
    const selectedTab = this.tabs.find((t) => t.id === this.selectedTabId)
    const sessions = selectedTab ? selectedTab.sessions : this.eventSessions

    if (this.showSoldOutTimes) {
      return sessions.map((session) => this.process(session))
    } else {
      return sessions.filter((item) => !item.sold_out).map((session) => this.process(session))
    }
  }

  get selected(): Item | void {
    // Process the input AnnotatedSession into an `Item` that is compatible with the template.
    return this.value ? this.process(this.value) : undefined
  }

  onSelect(session: AnnotatedSession) {
    if (!session.sold_out) this.$emit('input', session)
  }

  @Watch('value')
  onValueUpdate(value) {
    if (value) {
      this.$nextTick(() => {
        this.$el.scrollIntoView({ behavior: 'smooth' })
      })
    }
  }

  private process(session: AnnotatedSession): Item {
    return {
      session,
      label: session.startTime.format('h:mm A'),
      availability: this.availability(session),
      badges: this.badges(session),
    }
  }

  // TODO Move to <AvailabilityStatus>.
  private availability(session: AnnotatedSession): Item['availability'] {
    const low = session.availableCapacity < (this.capacityThreshold ?? 0)
    return {
      label: low ? `${session.availableCapacity} available` : 'Available',
      classes: { danger: low },
    }
  }

  private badges(session: AnnotatedSession): Badge[] {
    const result: Badge[] = []

    if (session.priceIncrease && session.priceIncrease > 0) {
      result.push({
        symbol: findCurrency(environment.portal.default_currency_code)?.symbol,
        style: 'increase',
      })
    }

    for (const annotation of session.annotations) {
      result.push({
        style: annotation.style,
      })
    }

    return result
  }

  private get tabs(): SessionTab[] {
    if (!this.config || !this.config.enabled) {
      return []
    }

    const { first_tab_name, second_tab_name, third_tab_name, first_tab_threshold, second_tab_threshold } = this.config

    /**
     * @param threshold military time format
     * @param min military time format
     */
    const sessionsByThreshold = (threshold: string, min: string = '0000'): AnnotatedSession[] => {
      return this.eventSessions.filter((s) => {
        const startTime = Number(s.startTime.format('HHmm'))
        return startTime >= Number(min) && startTime < Number(threshold)
      })
    }

    const configToTab = (name: string, threshold: string, prevThreshold?: string): SessionTab => {
      const sessions = sessionsByThreshold(threshold, prevThreshold)
      const subtitle = tabNote(sessions) ?? ''
      return {
        id: `${name}-${threshold}`,
        title: name,
        threshold: threshold,
        sessions: sessions,
        disabled: Boolean(subtitle),
        subtitle,
        classes: toKebabCase(subtitle),
      }
    }

    const tabNote = (sessions: AnnotatedSession[]): string | undefined => {
      if (sessions.length === 0) {
        return 'Not available'
      } else if (sessions.every((s) => s.sold_out)) {
        return 'Sold out'
      }

      return undefined
    }

    const lastMinuteOfDay = '2359'

    const res: SessionTab[] = [configToTab(first_tab_name, first_tab_threshold)]

    if (second_tab_name) {
      res.push(
        // TODO Should we check whether the `second_tab_threshold` is earlier than `first_tab_threshold`?
        configToTab(second_tab_name, second_tab_threshold ?? lastMinuteOfDay, first_tab_threshold),
      )
    }

    if (third_tab_name && second_tab_threshold) {
      res.push(configToTab(third_tab_name, lastMinuteOfDay, second_tab_threshold))
    }

    return res
  }
}
